import { LotteryParser } from "@sazka/lotteries/js/utils/lottery-parser";
import { DataLayer } from "@sazka/utils/data-layer";
import { ResponsiveUtils } from "@sazka/utils/responsive-utils";
import { Analytics } from "@/shared/utils/analytics/analytics";
import { AnalyticsPromo } from "@/shared/utils/analytics/analytics-promo";

// V2.0
export namespace ProductPageAnalytics {
    // DL1.10 - Game page
    export function pushGamePageDisplayed(lotteryName: string, isFirstLoad = false) {
        window.disablePageReady = true;
        window.disablePushPageResponsiveDesignMode = true;

        DataLayer.push({
            page: {
                type: "game",
                kind: "game",
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                gameCode: lotteryName,
                gameCodeStrict: lotteryName,
                responseDesignCategory: isFirstLoad ? ResponsiveUtils.getDataLayerResolution() : undefined,
            },
            event: isFirstLoad ? "pageReady" : "page",
        });
    }

    // DL1.10A Game detail page
    export function pushGameDetailPageDisplayed(lotteryName: string, isFirstLoad = false) {
        window.disablePageReady = true;
        window.disablePushPageResponsiveDesignMode = true;

        DataLayer.push({
            page: {
                type: "game.detail",
                kind: "game.detail",
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                gameCode: lotteryName,
                gameCodeStrict: lotteryName,
                responseDesignCategory: isFirstLoad ? ResponsiveUtils.getDataLayerResolution() : undefined,
            },
            event: isFirstLoad ? "pageReady" : "page",
        });
    }

    // DL1.10B Game checkbet page
    export function pushGameCheckbetPageDisplayed(lotteryName: string) {
        DataLayer.push({
            page: {
                type: "game.checkBet",
                kind: "game.checkBet",
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                gameCode: lotteryName,
                gameCodeStrict: lotteryName,
            },
        });
    }

    // DL1.10B Game how to play page
    export function pushGameHowToPlayPageDisplayed(lotteryName: string) {
        DataLayer.push({
            page: {
                type: "game.howToPlay",
                kind: "game.howToPlay",
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                gameCode: lotteryName,
                gameCodeStrict: lotteryName,
            },
        });
    }

    // DL1.11 Click navigation
    export function pushClickNavigation(itemName: string) {
        DataLayer.pushInstant({
            event: "action.click.navigation",
            action: {
                click: {
                    name: itemName,
                    eventProperty2: "sub",
                },
            },
        });
    }

    // DL1.12 Click Quicktips event
    export function pushClickQuicktips(
        bettingMode: string,
        lotteryAlias: string,
        position: string,
        wagerValue: number,
        label: string
    ) {
        const lotteryName = LotteryParser.analyticsCodeName(LotteryParser.parseLotteryName(lotteryAlias));

        DataLayer.push({
            event: "action.click.quicktips",
            action: {
                click: {
                    gameCode: lotteryName,
                    eventProperty2: position,
                    value: wagerValue,
                    label: label,
                },
            },
            page: {
                bettingMode: bettingMode,
                position: position,
            },
        });
    }

    // DL1.2 Game page banner impression
    export function pushGamePageBannerImpression(bannerTitleText: string, position = 1) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "game.carousel",
                            name: "game.carousel",
                            creative: bannerTitleText,
                            position: "bottom." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.21 Game page banner click
    export function pushGamePageBannerClick(bannerTitleText: string, position?: number) {
        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "game.carousel",
                            name: "game.carousel",
                            creative: bannerTitleText,
                            position: "bottom." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.22 Random tip click event
    export function pushRandomTipClick(gameCode: string, position: number, price: number, title: string) {
        DataLayer.push({
            event: "action.click.randomtips",
            action: {
                click: {
                    gameCode: gameCode,
                    eventProperty2: position.toString(),
                    value: price,
                    label: title,
                },
            },
        });
    }

    // DL1.3 Money bet
    export function pushMoneyBetStanding(data) {
        DataLayer.push({
            event: "action.bet.money",
            action: {
                bet: {
                    transactionId: data.transactionId,
                    gameCode: data.gameCode,
                    value: data.value,
                    bettingSystem: data.bettingSystem,
                    repetitionEndDate: data.repetitionEndDate,
                    columns: data.columns,
                    columnsFillType: data.columnsFillType,
                    columnsFillState: data.columnsFillState,
                    addonGame: data.addonGame,
                    bettingMode: data.bettingMode,
                    position: data.position,
                    tier: data.tier,
                },
            },
        });
    }

    export function pushMoneyBetSingleOrMultiDraw(data) {
        DataLayer.push({
            event: "action.bet.money",
            action: {
                bet: {
                    transactionId: data.transactionId,
                    gameCode: data.gameCode,
                    value: data.value,
                    bettingSystem: data.bettingSystem,
                    columns: data.columns,
                    columnsFillType: data.columnsFillType,
                    columnsFillState: data.columnsFillState,
                    addonGame: data.addonGame,
                    bettingMode: data.bettingMode,
                    position: data.position,
                    tier: data.tier,
                },
            },
        });
    }

    // DL1.3 & DL1.35 for random wagers widget implemented directly in code for this widget (_ds-random-wagers.ts)

    // DL1.35 Purchase page
    export function pushPurchase(data) {
        const { days, times } = Analytics.getDrawDaysAndTimes(data.drawNames);

        DataLayer.push({
            event: "page",
            page: {
                type: "purchase",
                kind: "purchase",
                verticalCode: "loterie",
                verticalCodeStrict: "loterie",
                gameCode: data.gameCode,
                gameCodeStrict: data.gameCode,
                bettingSystem: data.bettingSystem,
            },
            order: {
                id: data.orderId,
                totalValue: data.value,
                revenue: data.value,
                discount: 0,
                tax: 0,
            },
            cart: {
                products: [
                    {
                        fullPrice: data.value,
                        pocketPrice: data.value,
                        price: data.value,
                        tax: 0,
                        position: 1,
                        verticalCode: "loterie",
                        gameCode: data.gameCode,
                        boards: data.boards,
                        draws: data.draws,
                        addonGame: data.addonGame,
                        ticketKind: data.bettingSystem,
                        drawDays: days,
                        drawTimes: times,
                        bettingMode: data.bettingMode,
                    },
                ],
            },
        });
    }

    // DL1.40
    export const pushTicketCheckResult = (result: "win" | "loss", lottery: string) => {
        DataLayer.push({
            event: "action.checkBet.bet",
            action: {
                checkBet: {
                    bet: result,
                    gameCode: lottery,
                    verticalCode: "loterie",
                },
            },
        });
    };

    // DL1.41a
    // DL1.42a
    export const pushTicketCheckBannerImpress = (promo: AnalyticsPromo | null) => {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [promo],
                },
            },
        });
    };

    // DL1.41b
    // DL1.42b
    export const pushTicketCheckBannerClick = (promo: AnalyticsPromo | null) => {
        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [promo],
                },
            },
        });
    };

    // DL1.43
    export const pushTicketCheckButtonClick = (name: string) => {
        DataLayer.pushInstant({
            event: "action.click.navigation",
            action: {
                click: {
                    name: name,
                    eventProperty2: "page",
                },
            },
        });
    };

    // DL1.5 How to play page banner impression (Online sazenka)
    export function pushHowToPlayPageBannerImpressionOnlineBet(bannerTitle: string, position: number) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "game.howToPlayOnline.mainCarousel",
                            name: "game.howToPlayOnline.mainCarousel",
                            creative: bannerTitle,
                            position: "top." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.6 How to play page banner impression (Klasicka sazenka)
    export function pushHowToPlayPageBannerImpressionStandardBet(bannerTitle: string, position: number) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "game.howToPlayStandard.mainCarousel",
                            name: "game.howToPlayStandard.mainCarousel",
                            creative: bannerTitle,
                            position: "top." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.7 How to play banner impression
    export function pushHowToPlayBannerImpression(bannerTitle: string, position: number) {
        DataLayer.push({
            event: "action.impress.promos",
            action: {
                impress: {
                    promos: [
                        {
                            id: "game.howToPlay.subCarousel",
                            name: "game.howToPlay.subCarousel",
                            creative: bannerTitle,
                            position: "middle." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.7b How to play banner click
    export function pushHowToPlayBannerClick(bannerTitle: string, position: number) {
        DataLayer.pushInstant({
            event: "action.show.promos",
            action: {
                show: {
                    promos: [
                        {
                            id: "game.howToPlay.subCarousel",
                            name: "game.howToPlay.subCarousel",
                            creative: bannerTitle,
                            position: "middle." + position,
                        },
                    ],
                },
            },
        });
    }

    // DL1.8 Millionaires map
    export function pushMillionairesMapClick() {
        DataLayer.push({
            event: "action.clickmillionaresMap.loterie",
        });
    }

    // DL1.8 for quic lotteries - Stream banner click
    export function pushStreamBannerClick() {
        DataLayer.push({
            event: "action.playVideo.loterie",
            action: {
                playVideo: {
                    eventProperty1: "mainProductPage",
                },
            },
        });
    }
}
